import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Avatar,
  Card as MuiCard,
  CardContent,
  LinearProgress,
  IconButton,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  ViewList,
  ViewModule,
  ArrowBack,
  Search,
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  Remove,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTheme } from '@mui/material/styles';
import { useAppContext } from '../context/AppContext';
import { useClasificacion } from '../hooks/queries';
import Filters from '../components/Filters';
import { EmptyState, Skeleton } from '../design-system/components';
import colors from '../design-system/colors';
import spacing from '../design-system/spacing';
import shadows from '../design-system/shadows';
import usePageTitle from '../hooks/usePageTitle';
import usePageTour from '../hooks/usePageTour';
import { useAuth } from '../context/AuthContext';
import { LicensePreviewNotice } from '../components/license/LicenseGate';

const RankingEquiposPageModern = () => {
  usePageTitle('Ranking Equipos');
  usePageTour('rankingEquipos');
  const { t } = useTranslation('rankings');
  const navigate = useNavigate();
  const theme = useTheme();
  const { filters } = useAppContext();
  const { hasPermission } = useAuth();
  const canViewFullRanking = hasPermission('analytics.view');
  
  // Estados
  const [viewMode, setViewMode] = useState('table'); // 'table' o 'cards'
  const [orderBy, setOrderBy] = useState('puntos');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all'); // 'all', 'ataque', 'defensa', 'racha'

  const {
    data: rawData,
    isLoading,
    isError,
    refetch,
  } = useClasificacion(filters.temporada, null, filters.grupo, {
    enabled: !!(filters.temporada && filters.grupo),
  });

  const teams = useMemo(
    () => [...(rawData || [])].sort((a, b) => (b.puntos || 0) - (a.puntos || 0)),
    [rawData]
  );

  const handleApplyFilters = () => {
    refetch();
  };

  // Función para calcular valores computados
  const getComputedValue = (team, key) => {
    switch (key) {
      case 'puntos':
        return team.puntos || 0;
      case 'jugados':
        return team.jugados || 0;
      case 'ganados':
        return team.ganados || 0;
      case 'empatados':
        return team.empatados || 0;
      case 'perdidos':
        return team.perdidos || 0;
      case 'golesFavor':
        return team.golesFavor || team.golesfavor || 0;
      case 'golesContra':
        return team.golesContra || team.golescontra || 0;
      case 'diferencia':
        const gf = team.golesFavor || team.golesfavor || 0;
        const gc = team.golesContra || team.golescontra || 0;
        return gf - gc;
      case 'efectividad':
        const pj = team.jugados || 0;
        return pj > 0 ? ((team.puntos || 0) / (pj * 3) * 100) : 0;
      case 'promedioGolesFavor':
        const pjf = team.jugados || 0;
        return pjf > 0 ? ((team.golesFavor || team.golesfavor || 0) / pjf) : 0;
      case 'promedioGolesContra':
        const pjc = team.jugados || 0;
        return pjc > 0 ? ((team.golesContra || team.golescontra || 0) / pjc) : 0;
      default:
        return 0;
    }
  };

  // Filtrar y ordenar equipos
  const getFilteredAndSortedTeams = () => {
    if (!teams || teams.length === 0) return [];

    let filtered = [...teams];

    // Filtro por búsqueda
    if (searchTerm) {
      filtered = filtered.filter((t) =>
        (t.nombre || t.nombreequipo || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtro por categoría
    if (categoryFilter !== 'all') {
      if (categoryFilter === 'ataque') {
        // Equipos con más goles a favor
        filtered = filtered.filter((t) => getComputedValue(t, 'golesFavor') > 0);
      } else if (categoryFilter === 'defensa') {
        // Equipos con menos goles en contra
        filtered = filtered.filter((t) => getComputedValue(t, 'golesContra') >= 0);
      }
    }

    // Ordenar
    filtered.sort((a, b) => {
      const aValue = getComputedValue(a, orderBy);
      const bValue = getComputedValue(b, orderBy);
      
      if (orderDirection === 'asc') {
        return aValue - bValue;
      }
      return bValue - aValue;
    });

    return filtered;
  };

  const filteredTeams = getFilteredAndSortedTeams();
  const visibleTeams = canViewFullRanking ? filteredTeams : filteredTeams.slice(0, 5);

  // Handler para cambiar ordenamiento
  const handleSort = (column) => {
    if (!canViewFullRanking) return;
    if (orderBy === column) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(column);
      setOrderDirection('desc');
    }
  };

  // Categorías rápidas
  const categories = [
    { id: 'all', label: t('teams.categories.all'), icon: null },
    { id: 'ataque', label: t('teams.categories.attack'), orderBy: 'golesFavor' },
    { id: 'defensa', label: t('teams.categories.defense'), orderBy: 'golesContra', orderDir: 'asc' },
  ];

  const handleCategoryChange = (categoryId) => {
    setCategoryFilter(categoryId);
    const category = categories.find((c) => c.id === categoryId);
    if (category && category.orderBy) {
      setOrderBy(category.orderBy);
      setOrderDirection(category.orderDir || 'desc');
    } else if (categoryId === 'all') {
      // Al volver a "Todos", resetear a ordenamiento por puntos
      setOrderBy('puntos');
      setOrderDirection('desc');
    }
  };

  // Top 5 equipos para gráfico
  const top5Teams = filteredTeams.slice(0, 5);
  const maxValue = top5Teams.length > 0 ? getComputedValue(top5Teams[0], orderBy) : 1;

  // Función para obtener posición
  const getPositionChange = (index) => {
    // Aquí podrías comparar con posiciones anteriores si tuvieras esos datos
    // Por ahora retornamos neutral
    return 'same';
  };

  return (
    <Box className="wp-page" sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      {isError && (
        <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={handleApplyFilters}>{t('teams.errors.retry')}</Button>}>
          {t('teams.errors.loadData')}
        </Alert>
      )}
      {/* Header */}
      <Box
        className="wp-heading"
        data-tour="ranking-teams-header"
        sx={{
          bgcolor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Box sx={{ p: 3 }} data-tour="ranking-teams-filters">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <IconButton onClick={() => navigate('/dashboard')} aria-label={t('teams.backAriaLabel')} sx={{ color: colors.primary[600] }}>
              <ArrowBack />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography component="h1" variant="h4" fontWeight={700} color={theme.palette.text.primary}>
                {t('teams.headerTitle')}
              </Typography>
              <Typography variant="body2" color={theme.palette.text.secondary}>
                {t('teams.teamsFound', { count: filteredTeams.length })}
              </Typography>
            </Box>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table" aria-label={t('teams.tableView')}>
                <ViewList />
              </ToggleButton>
              <ToggleButton value="cards" aria-label={t('teams.cardsView')}>
                <ViewModule />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Filtros */}
          <Filters onApplyFilters={handleApplyFilters} />
        </Box>
      </Box>

      {/* Contenido */}
      <Box className="wp-body" sx={{ p: 3 }} data-tour="ranking-teams-results">
        {/* Categorías rápidas y buscador */}
        <Box sx={{ mb: 3 }} data-tour="ranking-teams-search">
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            {categories.map((cat) => (
              <Chip
                key={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {cat.icon && <span>{cat.icon}</span>}
                    <span>{cat.label}</span>
                  </Box>
                }
                onClick={() => handleCategoryChange(cat.id)}
                disabled={!canViewFullRanking}
                color={categoryFilter === cat.id ? 'primary' : 'default'}
                variant={categoryFilter === cat.id ? 'filled' : 'outlined'}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>

          <TextField
            fullWidth
            size="small"
            placeholder={t('teams.search.placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={!canViewFullRanking}
            sx={{ maxWidth: 400 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Top 5 Visual Bar Chart */}
        {viewMode === 'table' && top5Teams.length > 0 && (
          <Paper sx={{ mb: 3, p: 3, borderRadius: `${spacing.borderRadius.sm}px` }} data-tour="ranking-teams-chart">
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <EmojiEvents sx={{ color: colors.warning.main }} />
              {t('teams.top5Title', { category: categories.find((c) => c.id === categoryFilter)?.label || t('teams.defaultTop5Title') })}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {top5Teams.map((team, index) => {
                const value = getComputedValue(team, orderBy);
                const percentage = maxValue > 0 ? (value / maxValue) * 100 : 0;
                
                return (
                  <Box key={`${team.idequipo}-${team.nombre || index}`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          bgcolor: index === 0 ? colors.warning.main : theme.palette.action.selected,
                          color: index === 0 ? theme.palette.warning.contrastText : theme.palette.text.primary,
                          fontWeight: 700,
                          minWidth: 32,
                          flexShrink: 0,
                        }}
                      />
                      <Avatar
                        src={team.escudo}
                        alt={team.nombre || team.nombreequipo}
                        sx={{ width: 32, height: 32, flexShrink: 0 }}
                      >
                        {(team.nombre || team.nombreequipo || '').substring(0, 2)}
                      </Avatar>
                      <Typography 
                        variant="body2" 
                        fontWeight={500} 
                        sx={{ 
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {team.nombre || team.nombreequipo}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        fontWeight={700} 
                        color={colors.primary.main}
                        sx={{ flexShrink: 0 }}
                      >
                        {value.toFixed(orderBy.includes('promedio') || orderBy === 'efectividad' ? 2 : 0)}
                        {orderBy === 'efectividad' && '%'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={percentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: theme.palette.action.selected,
                        '& .MuiLinearProgress-bar': {
                          bgcolor: index === 0 ? colors.warning.main : colors.primary.main,
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Paper>
        )}

        {/* Loading */}
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rectangular" height={60} />
            ))}
          </Box>
        )}

        {/* Empty State */}
        {!isLoading && filteredTeams.length === 0 && (
          <EmptyState
            type="no-results"
            title={t('teams.emptyState.title')}
            description={t('teams.emptyState.description')}
          />
        )}

        {/* Vista Tabla */}
        {!isLoading && viewMode === 'table' && filteredTeams.length > 0 && (
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: `${spacing.borderRadius.sm}px`,
              overflowX: 'auto',
              maxWidth: '100%'
            }}
          >
            <Table sx={{ minWidth: 650, '& .MuiTableCell-root': { px: 2, py: 1.5 } }}>
              <TableHead sx={{ bgcolor: colors.neutral[50] }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 70 }}>{t('teams.table.position')}</TableCell>
                  <TableCell sx={{ width: '30%' }}>
                    <TableSortLabel
                      active={orderBy === 'nombre'}
                      direction={orderBy === 'nombre' ? orderDirection : 'asc'}
                      onClick={() => handleSort('nombre')}
                    >
                      {t('teams.table.team')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'jugados'}
                      direction={orderBy === 'jugados' ? orderDirection : 'asc'}
                      onClick={() => handleSort('jugados')}
                    >
                      {t('teams.table.matchesPlayed')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'ganados'}
                      direction={orderBy === 'ganados' ? orderDirection : 'asc'}
                      onClick={() => handleSort('ganados')}
                    >
                      {t('teams.table.won')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'empatados'}
                      direction={orderBy === 'empatados' ? orderDirection : 'asc'}
                      onClick={() => handleSort('empatados')}
                    >
                      {t('teams.table.drawn')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'perdidos'}
                      direction={orderBy === 'perdidos' ? orderDirection : 'asc'}
                      onClick={() => handleSort('perdidos')}
                    >
                      {t('teams.table.lost')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'golesFavor'}
                      direction={orderBy === 'golesFavor' ? orderDirection : 'asc'}
                      onClick={() => handleSort('golesFavor')}
                    >
                      {t('teams.table.goalsFor')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'golesContra'}
                      direction={orderBy === 'golesContra' ? orderDirection : 'asc'}
                      onClick={() => handleSort('golesContra')}
                    >
                      {t('teams.table.goalsAgainst')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'diferencia'}
                      direction={orderBy === 'diferencia' ? orderDirection : 'asc'}
                      onClick={() => handleSort('diferencia')}
                    >
                      {t('teams.table.diff')}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === 'puntos'}
                      direction={orderBy === 'puntos' ? orderDirection : 'asc'}
                      onClick={() => handleSort('puntos')}
                    >
                      {t('teams.table.points')}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleTeams.map((team, index) => {
                  const posChange = getPositionChange(index);
                  const diff = getComputedValue(team, 'diferencia');
                  
                  return (
                    <TableRow
                      key={`${team.idequipo}-${team.nombre || index}`}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.primary[50] } }}
                      onClick={() => navigate(`/estadisticasEquipos?id=${team.idequipo}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                              bgcolor: index < 3 ? colors.warning.light : theme.palette.action.selected,
                              color: index < 3 ? colors.warning.dark : theme.palette.text.secondary,
                              fontWeight: 600,
                            }}
                          />
                          {posChange === 'up' && <TrendingUp sx={{ fontSize: 16, color: colors.success.main }} />}
                          {posChange === 'down' && <TrendingDown sx={{ fontSize: 16, color: colors.error.main }} />}
                          {posChange === 'same' && <Remove sx={{ fontSize: 16, color: theme.palette.text.disabled }} />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Avatar
                            src={team.escudo}
                            alt={team.nombre || team.nombreequipo}
                            sx={{ width: 32, height: 32, flexShrink: 0 }}
                          >
                            {(team.nombre || team.nombreequipo || '').substring(0, 2)}
                          </Avatar>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0
                            }}
                          >
                            {team.nombre || team.nombreequipo}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{getComputedValue(team, 'jugados')}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={colors.football.win} fontWeight={600}>
                          {getComputedValue(team, 'ganados')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={colors.football.draw}>
                          {getComputedValue(team, 'empatados')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={colors.football.loss}>
                          {getComputedValue(team, 'perdidos')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{getComputedValue(team, 'golesFavor')}</TableCell>
                      <TableCell align="right">{getComputedValue(team, 'golesContra')}</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={diff > 0 ? colors.football.win : diff < 0 ? colors.football.loss : theme.palette.text.secondary}
                        >
                          {diff > 0 ? '+' : ''}{diff}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6" fontWeight={700} color={colors.primary.main}>
                          {getComputedValue(team, 'puntos')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Vista Cards */}
        {!isLoading && viewMode === 'cards' && filteredTeams.length > 0 && (
          <Grid container spacing={2}>
            {visibleTeams.map((team, index) => {
              const diff = getComputedValue(team, 'diferencia');
              const efectividad = getComputedValue(team, 'efectividad');
              
              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`${team.idequipo}-${team.nombre || index}`}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <MuiCard
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        borderRadius: `${spacing.borderRadius.sm}px`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: shadows.cardHover,
                        },
                      }}
                      onClick={() => navigate(`/estadisticasEquipos?id=${team.idequipo}`)}
                    >
                      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Avatar
                            src={team.escudo}
                            alt={team.nombre || team.nombreequipo}
                            sx={{ width: 64, height: 64 }}
                          >
                            {(team.nombre || team.nombreequipo || '').substring(0, 2)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              sx={{
                                bgcolor: index < 3 ? colors.warning.light : theme.palette.action.selected,
                                color: index < 3 ? colors.warning.dark : theme.palette.text.secondary,
                                fontWeight: 700,
                                mb: 1,
                              }}
                            />
                            <Typography 
                              variant="h6" 
                              fontWeight={600} 
                              sx={{ 
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {team.nombre || team.nombreequipo}
                            </Typography>
                          </Box>
                          {index < 3 && (
                            <EmojiEvents sx={{ color: colors.warning.main, fontSize: 28 }} />
                          )}
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, mb: 2 }}>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.won')}
                             </Typography>
                             <Typography variant="h6" fontWeight={700} color={colors.football.win}>
                               {getComputedValue(team, 'ganados')}
                             </Typography>
                           </Box>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.drawn')}
                             </Typography>
                             <Typography variant="h6" fontWeight={700} color={colors.football.draw}>
                               {getComputedValue(team, 'empatados')}
                             </Typography>
                           </Box>
                           <Box sx={{ textAlign: 'center' }}>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.lost')}
                             </Typography>
                             <Typography variant="h6" fontWeight={700} color={colors.football.loss}>
                               {getComputedValue(team, 'perdidos')}
                             </Typography>
                           </Box>
                         </Box>

                         <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                           <Box>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.points')}
                             </Typography>
                             <Typography variant="h5" fontWeight={700} color={colors.primary.main}>
                               {getComputedValue(team, 'puntos')}
                             </Typography>
                           </Box>
                           <Box>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.diff')}
                             </Typography>
                             <Typography
                               variant="h5"
                               fontWeight={700}
                               color={diff > 0 ? colors.football.win : diff < 0 ? colors.football.loss : theme.palette.text.secondary}
                             >
                               {diff > 0 ? '+' : ''}{diff}
                             </Typography>
                           </Box>
                           <Box>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.goalsForAgainst')}
                             </Typography>
                             <Typography variant="body2" fontWeight={600}>
                               {getComputedValue(team, 'golesFavor')} / {getComputedValue(team, 'golesContra')}
                             </Typography>
                           </Box>
                           <Box>
                             <Typography variant="caption" color={theme.palette.text.secondary}>
                               {t('teams.cardStats.efficiency')}
                             </Typography>
                             <Typography variant="body2" fontWeight={600}>
                               {efectividad.toFixed(1)}%
                             </Typography>
                           </Box>
                         </Box>
                      </CardContent>
                    </MuiCard>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!isLoading && !canViewFullRanking && filteredTeams.length > 5 && (
          <LicensePreviewNotice permission="analytics.view" visibleCount={5} />
        )}
      </Box>
    </Box>
  );
};

export default RankingEquiposPageModern;
